import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Popover,
  Snackbar,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PersonIcon from '@mui/icons-material/Person';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import DownloadIcon from '@mui/icons-material/Download';
import { useAuth } from '../contexts/AuthContext';
import { getAcceptLanguageHeader, useI18n } from '../i18n';
import InactiveCustomersTab from '../components/customers/InactiveCustomersTab';

const PAGE_SIZE = 50;
const BALANCE_CURRENCIES = ['rub', 'usd', 'eur', 'try', 'cny', 'kzt'];

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(String(value).replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function formatBalanceAmount(value) {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount.toFixed(2) : '0.00';
}

function normalizeBalanceInput(value) {
  const normalized = String(value || '').replace(',', '.');
  const hasDecimalSeparator = normalized.includes('.');
  const [integerPart = '', ...decimalParts] = normalized.split('.');
  const integerDigits = integerPart.replace(/\D/g, '').slice(0, 9);
  const decimalDigits = decimalParts.join('').replace(/\D/g, '').slice(0, 2);

  return hasDecimalSeparator ? `${integerDigits}.${decimalDigits}` : integerDigits;
}

function formatBalanceDelta(currentValue, initialValue) {
  const current = Number(currentValue);
  const initial = Number(initialValue);
  if (!Number.isFinite(current) || !Number.isFinite(initial)) {
    return '';
  }

  const delta = Math.round((current - initial) * 100) / 100;
  if (delta === 0) {
    return '';
  }

  const sign = delta > 0 ? '+' : '-';
  return `${sign}${Math.abs(delta).toFixed(2)}`;
}

function balanceAfterOperation(currentValue, adjustmentValue, direction) {
  const currentCents = Math.round(Number(currentValue) * 100);
  const adjustmentCents = Math.round(Number(adjustmentValue) * 100);
  if (!Number.isFinite(currentCents) || !Number.isFinite(adjustmentCents) || adjustmentCents <= 0) {
    return null;
  }

  const nextCents = direction === 'credit'
    ? currentCents + adjustmentCents
    : currentCents - adjustmentCents;

  return nextCents < 0 ? null : (nextCents / 100).toFixed(2);
}

function normalizeBalances(items) {
  const byCurrency = new Map(
    (Array.isArray(items) ? items : []).map(balance => [
      String(balance?.currency || '').toLowerCase(),
      formatBalanceAmount(balance?.amount),
    ])
  );

  return BALANCE_CURRENCIES.map(currency => ({
    currency,
    amount: byCurrency.get(currency) || '0.00',
  }));
}

function balanceFormFromBalances(items) {
  return normalizeBalances(items).reduce((form, balance) => ({
    ...form,
    [balance.currency]: balance.amount,
  }), {});
}

function customerFullName(customer) {
  const parts = [customer?.first_name, customer?.last_name]
    .map(value => String(value || '').trim())
    .filter(Boolean);
  return parts.length ? parts.join(' ') : '—';
}

const STATUS_CONFIG = {
  active:  { bg: '#dcfce7', color: '#166534', dot: '#22c55e', label: 'active' },
  blocked: { bg: '#fee2e2', color: '#991b1b', dot: '#ef4444', label: 'blocked' },
  default: { bg: '#f3f4f6', color: '#374151', dot: '#9ca3af', label: '—' },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.default;
  return (
    <Box
      component="span"
      onClick={e => { e.preventDefault(); e.stopPropagation(); }}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '5px',
        px: 1.25,
        py: 0.35,
        borderRadius: '20px',
        backgroundColor: cfg.bg,
        color: cfg.color,
        fontSize: '0.75rem',
        fontWeight: 600,
        letterSpacing: '0.02em',
        whiteSpace: 'nowrap',
        cursor: 'default',
        userSelect: 'none',
      }}
    >
      <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: cfg.dot, flexShrink: 0 }} />
      {status || '—'}
    </Box>
  );
}

function CustomerAvatar({ customer, size = 36 }) {
  const name = customerFullName(customer);
  const initials = name !== '—'
    ? name.split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase()
    : null;
  const colors = ['#6366f1','#8b5cf6','#ec4899','#f97316','#14b8a6','#3b82f6','#84cc16'];
  const colorIndex = (customer?.id || 0) % colors.length;
  return (
    <Avatar
      sx={{
        width: size,
        height: size,
        fontSize: size * 0.38,
        fontWeight: 700,
        backgroundColor: colors[colorIndex],
        flexShrink: 0,
      }}
    >
      {initials || <PersonIcon sx={{ fontSize: size * 0.6 }} />}
    </Avatar>
  );
}

export default function CustomersPage() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [customers, setCustomers] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: PAGE_SIZE, total: 0 });
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [orders, setOrders] = useState([]);
  const [ordersPagination, setOrdersPagination] = useState({ page: 1, limit: PAGE_SIZE, total: 0 });
  const [balances, setBalances] = useState(normalizeBalances([]));
  const [balanceForm, setBalanceForm] = useState(balanceFormFromBalances([]));
  const [balanceSavingCurrency, setBalanceSavingCurrency] = useState('');
  const [balanceExpanded, setBalanceExpanded] = useState(false);
  const [balanceOperation, setBalanceOperation] = useState({ anchorEl: null, currency: '', amount: '' });
  const [contactForm, setContactForm] = useState({ email: '', phone: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const request = useCallback(async (path, options = {}) => {
    if (!user?.api_token) {
      throw new Error(t('Нет токена'));
    }

    const res = await fetch(path, {
      ...options,
      headers: {
        ...(options.body ? { 'Content-Type': 'application/json' } : {}),
        Authorization: `Bearer ${user.api_token}`,
        'Accept-Language': getAcceptLanguageHeader(),
        ...(options.headers || {}),
      },
    });
    const payload = await res.json().catch(() => ({}));
    if (!res.ok || payload?.success === false) {
      throw new Error(payload?.error || t('Ошибка запроса'));
    }

    return payload.data;
  }, [t, user?.api_token]);

  const loadCustomers = useCallback(async (page = 1) => {
    if (!user?.api_token || user.role !== 'admin') return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(PAGE_SIZE),
      });
      const search = searchTerm.trim();
      if (search) {
        params.set('search', search);
      }
      const data = await request(`/api/customers?${params.toString()}`);
      setCustomers(Array.isArray(data.items) ? data.items : []);
      setPagination(data.pagination || { page, limit: PAGE_SIZE, total: 0 });
    } catch (e) {
      showSnackbar(e.message || t('Ошибка загрузки клиентов'), 'error');
    } finally {
      setLoading(false);
    }
  }, [request, searchTerm, showSnackbar, t, user]);

  const downloadCustomersCsv = async () => {
    if (!user?.api_token) return;
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      const search = searchTerm.trim();
      if (search) {
        params.set('search', search);
      }
      const query = params.toString();
      const response = await fetch(`/api/customers/export${query ? `?${query}` : ''}`, {
        headers: {
          Authorization: `Bearer ${user.api_token}`,
          'Accept-Language': getAcceptLanguageHeader(),
        },
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload?.error || t('Ошибка выгрузки CSV'));
      }

      const blob = await response.blob();
      const disposition = response.headers.get('Content-Disposition') || '';
      const filename = disposition.match(/filename="?([^"]+)"?/i)?.[1] || 'customers.csv';
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showSnackbar(t('CSV выгружен'));
    } catch (e) {
      showSnackbar(e.message || t('Ошибка выгрузки CSV'), 'error');
    } finally {
      setDownloading(false);
    }
  };

  const applyCustomerData = useCallback((customer, ordersData, balancesData) => {
    const normalizedBalances = normalizeBalances(balancesData);

    setSelectedCustomer(customer);
    setContactForm({
      email: customer?.email || '',
      phone: customer?.phone || '',
    });
    setBalances(normalizedBalances);
    setBalanceForm(balanceFormFromBalances(normalizedBalances));
    setOrders(Array.isArray(ordersData?.items) ? ordersData.items : []);
    setOrdersPagination(ordersData?.pagination || { page: 1, limit: PAGE_SIZE, total: 0 });
  }, []);

  const loadCustomer = useCallback(async (id, ordersPage = 1) => {
    setDetailLoading(true);
    try {
      if (ordersPage === 1) {
        const data = await request(`/api/customers/${id}`);
        applyCustomerData(data.customer, data.orders, data.balances);
      } else {
        const data = await request(`/api/customers/${id}/orders?page=${ordersPage}&limit=${PAGE_SIZE}`);
        setOrders(Array.isArray(data.items) ? data.items : []);
        setOrdersPagination(data.pagination || { page: ordersPage, limit: PAGE_SIZE, total: 0 });
      }
    } catch (e) {
      showSnackbar(e.message || t('Ошибка загрузки клиента'), 'error');
    } finally {
      setDetailLoading(false);
    }
  }, [applyCustomerData, request, showSnackbar, t]);

  useEffect(() => {
    const timer = setTimeout(() => {
      loadCustomers(1);
    }, 550);

    return () => clearTimeout(timer);
  }, [loadCustomers]);

  if (!user || user.role !== 'admin') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 4, p: 2, bgcolor: '#fee2e2', borderRadius: 2, color: '#991b1b', fontWeight: 600 }}>
        <CancelIcon />
        {t('Доступ запрещён. Только для администратора.')}
      </Box>
    );
  }

  const selectCustomer = (customer) => {
    setSelectedCustomerId(customer.id);
    setSelectedCustomer(null);
    setOrders([]);
    setBalances(normalizeBalances([]));
    setBalanceForm(balanceFormFromBalances([]));
    setBalanceExpanded(false);
    setBalanceOperation({ anchorEl: null, currency: '', amount: '' });
    loadCustomer(customer.id);
  };

  const handleBack = () => {
    setSelectedCustomerId(null);
    setSelectedCustomer(null);
    setOrders([]);
    setBalances(normalizeBalances([]));
    setBalanceForm(balanceFormFromBalances([]));
    setBalanceExpanded(false);
    setBalanceOperation({ anchorEl: null, currency: '', amount: '' });
  };

  const handleSaveContact = async () => {
    if (!selectedCustomerId) return;
    setActionLoading(true);
    try {
      const customer = await request(`/api/customers/${selectedCustomerId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          email: contactForm.email.trim(),
          phone: contactForm.phone.trim(),
        }),
      });
      setSelectedCustomer(customer);
      setContactForm({ email: customer.email || '', phone: customer.phone || '' });
      setCustomers(prev => prev.map(item => (item.id === customer.id ? { ...item, ...customer } : item)));
      showSnackbar(t('Контакты клиента обновлены'));
    } catch (e) {
      showSnackbar(e.message || t('Ошибка обновления клиента'), 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveBalance = async (currency, amount = balanceForm[currency]) => {
    if (!selectedCustomerId) return false;
    setBalanceSavingCurrency(currency);
    try {
      const data = await request(`/api/customers/${selectedCustomerId}/balances/${currency}`, {
        method: 'PATCH',
        body: JSON.stringify({
          amount,
        }),
      });
      const normalizedBalances = normalizeBalances(data.balances);
      setBalances(normalizedBalances);
      setBalanceForm(balanceFormFromBalances(normalizedBalances));
      showSnackbar(t('Баланс клиента обновлён'));
      return true;
    } catch (e) {
      showSnackbar(e.message || t('Ошибка обновления баланса'), 'error');
      return false;
    } finally {
      setBalanceSavingCurrency('');
    }
  };

  const handleBalanceOperation = async (direction) => {
    const { currency, amount } = balanceOperation;
    const adjustmentCents = Math.round(Number(amount) * 100);
    const currentBalance = balances.find(item => item.currency === currency);
    const currentCents = Math.round(Number(currentBalance?.amount || 0) * 100);

    if (!Number.isFinite(adjustmentCents) || adjustmentCents <= 0) {
      showSnackbar(t('Введите сумму больше нуля'), 'error');
      return;
    }

    const nextCents = direction === 'credit'
      ? currentCents + adjustmentCents
      : currentCents - adjustmentCents;

    if (nextCents < 0) {
      showSnackbar(t('Сумма списания превышает текущий баланс'), 'error');
      return;
    }

    const nextAmount = (nextCents / 100).toFixed(2);
    setBalanceForm(prev => ({ ...prev, [currency]: nextAmount }));
    const saved = await handleSaveBalance(currency, nextAmount);
    if (saved) {
      setBalanceOperation({ anchorEl: null, currency: '', amount: '' });
    }
  };

  const handleBlockToggle = async () => {
    if (!selectedCustomerId || !selectedCustomer) return;
    const action = selectedCustomer.status === 'blocked' ? 'unblock' : 'block';
    setActionLoading(true);
    try {
      const customer = await request(`/api/customers/${selectedCustomerId}/${action}`, { method: 'POST' });
      setSelectedCustomer(customer);
      setCustomers(prev => prev.map(item => (item.id === customer.id ? { ...item, ...customer } : item)));
      showSnackbar(action === 'block' ? t('Клиент заблокирован') : t('Клиент разблокирован'));
    } catch (e) {
      showSnackbar(e.message || t('Ошибка изменения статуса клиента'), 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirmEmail = async () => {
    if (!selectedCustomerId) return;
    setActionLoading(true);
    try {
      const customer = await request(`/api/customers/${selectedCustomerId}/confirm-email`, { method: 'POST' });
      setSelectedCustomer(customer);
      setCustomers(prev => prev.map(item => (item.id === customer.id ? { ...item, ...customer } : item)));
      showSnackbar(t('Почта подтверждена'));
    } catch (e) {
      showSnackbar(e.message || t('Ошибка подтверждения почты'), 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const operationCurrentAmount = balances.find(
    item => item.currency === balanceOperation.currency
  )?.amount || '0.00';
  const operationCreditAmount = balanceAfterOperation(
    operationCurrentAmount,
    balanceOperation.amount,
    'credit'
  );
  const operationDebitAmount = balanceAfterOperation(
    operationCurrentAmount,
    balanceOperation.amount,
    'debit'
  );

  if (selectedCustomerId) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <IconButton onClick={handleBack} size="small" sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Typography variant="body2" sx={{ color: 'text.secondary', cursor: 'pointer', '&:hover': { color: 'text.primary' } }} onClick={handleBack}>
            {t('Назад к клиентам')}
          </Typography>
        </Box>

        {detailLoading && !selectedCustomer ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 8 }}>
            <CircularProgress />
          </Box>
        ) : selectedCustomer ? (
          <Stack spacing={2.5}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'flex-start' }}>
                <Stack direction="row" spacing={2.5} alignItems="flex-start">
                  <CustomerAvatar customer={selectedCustomer} size={56} />
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, mb: 0.5 }}>
                      {customerFullName(selectedCustomer)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
                      ID #{selectedCustomer.id}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                      <StatusBadge status={selectedCustomer.status} />
                      <Tooltip title={selectedCustomer.email_verified ? t('Email подтверждён') : t('Email не подтверждён')}>
                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ cursor: 'default' }}>
                          {selectedCustomer.email_verified
                            ? <CheckCircleIcon sx={{ fontSize: 16, color: '#22c55e' }} />
                            : <CancelIcon sx={{ fontSize: 16, color: '#d1d5db' }} />}
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>Email</Typography>
                        </Stack>
                      </Tooltip>
                      <Tooltip title={selectedCustomer.phone_verified ? t('Телефон подтверждён') : t('Телефон не подтверждён')}>
                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ cursor: 'default' }}>
                          {selectedCustomer.phone_verified
                            ? <CheckCircleIcon sx={{ fontSize: 16, color: '#22c55e' }} />
                            : <CancelIcon sx={{ fontSize: 16, color: '#d1d5db' }} />}
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>{t('Телефон')}</Typography>
                        </Stack>
                      </Tooltip>
                    </Stack>
                  </Box>
                </Stack>

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ flexShrink: 0 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    color={selectedCustomer.status === 'blocked' ? 'success' : 'error'}
                    onClick={handleBlockToggle}
                    disabled={actionLoading}
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                  >
                    {selectedCustomer.status === 'blocked' ? t('Разблокировать') : t('Заблокировать')}
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleConfirmEmail}
                    disabled={actionLoading || !selectedCustomer.email || selectedCustomer.email_verified}
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                  >
                    {t('Подтвердить почту')}
                  </Button>
                </Stack>
              </Stack>

              <Divider sx={{ my: 2.5 }} />

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2.5 }}>
                <TextField
                  label="Email"
                  value={contactForm.email}
                  onChange={e => setContactForm(prev => ({ ...prev, email: e.target.value }))}
                  fullWidth
                  disabled={actionLoading}
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
                <TextField
                  label={t('Телефон')}
                  value={contactForm.phone}
                  onChange={e => setContactForm(prev => ({ ...prev, phone: e.target.value }))}
                  fullWidth
                  disabled={actionLoading}
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
                <Button
                  variant="contained"
                  onClick={handleSaveContact}
                  disabled={actionLoading}
                  sx={{ minWidth: 160, borderRadius: 2, textTransform: 'none', fontWeight: 600, flexShrink: 0 }}
                >
                  {t('Сохранить')}
                </Button>
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.25 }}>{t('Дата регистрации')}</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{formatDate(selectedCustomer.created_at)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.25 }}>{t('Последний вход')}</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{formatDate(selectedCustomer.last_login_at)}</Typography>
                </Box>
              </Stack>
            </Paper>

            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {t('Баланс')}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setBalanceExpanded(prev => !prev)}
                  aria-label={balanceExpanded ? t('Свернуть баланс') : t('Развернуть баланс')}
                  aria-expanded={balanceExpanded}
                  sx={{
                    transform: balanceExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s',
                  }}
                >
                  <KeyboardArrowDownIcon fontSize="small" />
                </IconButton>
              </Stack>
              <Collapse in={balanceExpanded} timeout="auto" unmountOnExit>
                <Stack spacing={1.5} sx={{ mt: 2 }}>
                  {balances.map(balance => {
                    const currency = balance.currency;
                    const saving = balanceSavingCurrency === currency;
                    const delta = formatBalanceDelta(balanceForm[currency], balance.amount);

                    return (
                      <Stack
                        key={currency}
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1.5}
                        alignItems={{ xs: 'stretch', sm: 'center' }}
                      >
                        <Box sx={{ minWidth: 104, pr: 1.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>
                            {currency}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {formatBalanceAmount(balance.amount)}
                          </Typography>
                        </Box>
                        <TextField
                          label={t('Сумма')}
                          value={balanceForm[currency] ?? '0.00'}
                          onChange={e => setBalanceForm(prev => ({ ...prev, [currency]: normalizeBalanceInput(e.target.value) }))}
                          onBlur={() => {
                            if (String(balanceForm[currency] ?? '').trim() === '') {
                              setBalanceForm(prev => ({ ...prev, [currency]: balance.amount }));
                            }
                          }}
                          disabled={saving}
                          size="small"
                          type="text"
                          inputProps={{ inputMode: 'decimal', pattern: '[0-9]*[.,]?[0-9]{0,2}', maxLength: 12 }}
                          sx={{
                            width: { xs: '100%', sm: '42%' },
                            '& .MuiOutlinedInput-root': { borderRadius: 2 },
                          }}
                        />
                        <Button
                          variant="contained"
                          onClick={() => handleSaveBalance(currency)}
                          disabled={saving}
                          sx={{ minWidth: 140, borderRadius: 2, textTransform: 'none', fontWeight: 600, flexShrink: 0 }}
                        >
                          {saving ? <CircularProgress size={18} color="inherit" /> : t('Сохранить')}
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={event => setBalanceOperation({
                            anchorEl: event.currentTarget,
                            currency,
                            amount: '',
                          })}
                          disabled={saving}
                          sx={{ minWidth: 120, borderRadius: 2, textTransform: 'none', fontWeight: 600, flexShrink: 0 }}
                        >
                          {t('Операция')}
                        </Button>
                        <Typography
                          variant="body2"
                          sx={{
                            minWidth: 72,
                            fontWeight: 700,
                            color: delta.startsWith('+') ? '#16a34a' : delta.startsWith('-') ? '#dc2626' : 'text.secondary',
                            textAlign: { xs: 'left', sm: 'right' },
                          }}
                        >
                          {delta || ' '}
                        </Typography>
                      </Stack>
                    );
                  })}
                </Stack>
              </Collapse>
            </Paper>

            <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
              <Box sx={{ px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {t('Заказы клиента')}
                  {ordersPagination.total > 0 && (
                    <Box component="span" sx={{ ml: 1, px: 1, py: 0.25, borderRadius: '12px', bgcolor: '#f3f4f6', color: 'text.secondary', fontSize: '0.75rem', fontWeight: 600 }}>
                      {ordersPagination.total}
                    </Box>
                  )}
                </Typography>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#fafafa' }}>
                      <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>ID</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('Внешний ID')}</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('Дата')}</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('Сервис')}</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('Сумма')}</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('Телефон')}</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Email</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('Статус')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detailLoading ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                          <CircularProgress size={24} />
                        </TableCell>
                      </TableRow>
                    ) : orders.length ? (
                      orders.map(order => (
                        <TableRow key={order.id} sx={{ '&:hover': { bgcolor: '#fafafa' } }}>
                          <TableCell sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>{order.id}</TableCell>
                          <TableCell sx={{ fontSize: '0.8125rem' }}>{order.external_order_id || '—'}</TableCell>
                          <TableCell sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>{formatDate(order.created_at || order.payment_time)}</TableCell>
                          <TableCell sx={{ fontSize: '0.8125rem' }}>{order.service_name || '—'}</TableCell>
                          <TableCell sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>{order.amount_src || order.amount_dist || '—'} {order.currency_src || ''}</TableCell>
                          <TableCell sx={{ fontSize: '0.8125rem' }}>{order.phone_dist || '—'}</TableCell>
                          <TableCell sx={{ fontSize: '0.8125rem' }}>{order.email || '—'}</TableCell>
                          <TableCell><StatusBadge status={order.order_status} /></TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 5, color: 'text.secondary' }}>{t('Заказы не найдены')}</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={ordersPagination.total || 0}
                page={Math.max(0, (ordersPagination.page || 1) - 1)}
                rowsPerPage={PAGE_SIZE}
                rowsPerPageOptions={[PAGE_SIZE]}
                onPageChange={(_, nextPage) => loadCustomer(selectedCustomerId, nextPage + 1)}
                labelRowsPerPage={t('Строк на странице:')}
              />
            </Paper>
          </Stack>
        ) : null}

        <Popover
          open={Boolean(balanceOperation.anchorEl)}
          anchorEl={balanceOperation.anchorEl}
          onClose={() => setBalanceOperation({ anchorEl: null, currency: '', amount: '' })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{ paper: { sx: { mt: 1, p: 2, width: 300, borderRadius: 2.5 } } }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {t('Операция с балансом')} {balanceOperation.currency.toUpperCase()}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.25, mb: 1.5 }}>
            {t('Текущий баланс')}: {formatBalanceAmount(operationCurrentAmount)}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label={t('Сумма операции')}
            value={balanceOperation.amount}
            onChange={event => setBalanceOperation(prev => ({
              ...prev,
              amount: normalizeBalanceInput(event.target.value),
            }))}
            disabled={Boolean(balanceSavingCurrency)}
            type="text"
            inputProps={{ inputMode: 'decimal', pattern: '[0-9]*[.,]?[0-9]{0,2}', maxLength: 12 }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
          {balanceOperation.amount && (
            <Stack spacing={0.25} sx={{ mt: 1 }}>
              <Typography variant="caption" sx={{ color: 'success.main' }}>
                {t('После пополнения')}: {operationCreditAmount || '—'}
              </Typography>
              <Typography variant="caption" sx={{ color: operationDebitAmount ? 'text.secondary' : 'error.main' }}>
                {t('После списания')}: {operationDebitAmount || t('недостаточно средств')}
              </Typography>
            </Stack>
          )}
          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              color="error"
              disabled={Boolean(balanceSavingCurrency)}
              onClick={() => handleBalanceOperation('debit')}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
            >
              {t('Списать')}
            </Button>
            <Button
              fullWidth
              variant="contained"
              color="success"
              disabled={Boolean(balanceSavingCurrency)}
              onClick={() => handleBalanceOperation('credit')}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
            >
              {balanceSavingCurrency
                ? <CircularProgress size={18} color="inherit" />
                : t('Пополнить')}
            </Button>
          </Stack>
        </Popover>

        <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{t('Клиенты')}</Typography>
          {activeTab === 0 && pagination.total > 0 && (
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
              {pagination.total} {t('всего')}
            </Typography>
          )}
        </Box>
        {activeTab === 0 && (
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={downloading ? <CircularProgress size={16} /> : <DownloadIcon />}
              onClick={downloadCustomersCsv}
              disabled={downloading || loading}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {t('Скачать CSV')}
            </Button>
            <Tooltip title={t('Обновить')}>
              <IconButton
                onClick={() => loadCustomers(pagination.page || 1)}
                disabled={loading}
                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}
              >
                <RefreshIcon fontSize="small" sx={{ transition: 'transform 0.4s', ...(loading && { animation: 'spin 1s linear infinite', '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } } }) }} />
              </IconButton>
            </Tooltip>
          </Stack>
        )}
      </Box>

      <Tabs
        value={activeTab}
        onChange={(_, value) => setActiveTab(value)}
        sx={{ mb: 2, borderBottom: '1px solid', borderColor: 'divider' }}
      >
        <Tab label={t('Все клиенты')} />
        <Tab label={t('Неактивные клиенты')} />
      </Tabs>

      {activeTab === 0 ? (
        <>
          <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          label={t('Поиск клиентов')}
          placeholder={t('Номер, email, имя или фамилия')}
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          disabled={loading}
        />
          </Box>

          <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#fafafa' }}>
                <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('Клиент')}</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('Телефон')}</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('Дата регистрации')}</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('Статус')}</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('Подтверждения')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : customers.length ? (
                customers.map(customer => (
                  <TableRow
                    key={customer.id}
                    onClick={() => selectCustomer(customer)}
                    sx={{
                      cursor: 'pointer',
                      transition: 'background-color 0.15s',
                      '&:hover': { bgcolor: '#f9fafb' },
                      '&:hover .row-arrow': { opacity: 1 },
                    }}
                  >
                    <TableCell>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <CustomerAvatar customer={customer} size={34} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                            {customerFullName(customer)}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            #{customer.id}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem' }}>{customer.email || '—'}</TableCell>
                    <TableCell sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>{customer.phone || '—'}</TableCell>
                    <TableCell sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>{formatDate(customer.created_at)}</TableCell>
                    <TableCell>
                      <StatusBadge status={customer.status} />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Tooltip title={customer.email_verified ? t('Email подтверждён') : t('Email не подтверждён')}>
                          <Stack direction="row" spacing={0.4} alignItems="center" sx={{ cursor: 'default' }}>
                            {customer.email_verified
                              ? <CheckCircleIcon sx={{ fontSize: 16, color: '#22c55e' }} />
                              : <CancelIcon sx={{ fontSize: 16, color: '#d1d5db' }} />}
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Email</Typography>
                          </Stack>
                        </Tooltip>
                        <Tooltip title={customer.phone_verified ? t('Телефон подтверждён') : t('Телефон не подтверждён')}>
                          <Stack direction="row" spacing={0.4} alignItems="center" sx={{ cursor: 'default' }}>
                            {customer.phone_verified
                              ? <CheckCircleIcon sx={{ fontSize: 16, color: '#22c55e' }} />
                              : <CancelIcon sx={{ fontSize: 16, color: '#d1d5db' }} />}
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>{t('Тел.')}</Typography>
                          </Stack>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6, color: 'text.secondary' }}>{t('Клиенты не найдены')}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={pagination.total || 0}
          page={Math.max(0, (pagination.page || 1) - 1)}
          rowsPerPage={PAGE_SIZE}
          rowsPerPageOptions={[PAGE_SIZE]}
          onPageChange={(_, nextPage) => loadCustomers(nextPage + 1)}
          labelRowsPerPage={t('Строк на странице:')}
        />
          </Paper>
        </>
      ) : (
        <InactiveCustomersTab token={user.api_token} t={t} />
      )}

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
